/**
 * 1·2학기 AI 범교과 자동 구성 (마스터 관리자 전용)
 * - 범교과 주제 등록(1·2학기 편성 시수) → 남은 시수 = 편성 시수 − 이미 배정된 범교과 수(카운팅 모달과 같은 집계, 동기화 미러 제외)
 * - AI 가 범교과가 빈 수업 행 중 어울리는 행을 골라 주제어·학습내용 제안 → 표에서 편집/삭제 → '범교과 반영하기' 로
 *   수업 행의 범교과 칸(동기화 편제는 대표 내용 행)에 수동 입력과 같은 저장 경로로 기록
 */
import React from 'react';
import AiSparkIcon from '../common/AiSparkIcon';
import AiPresetImportModal from '../aiPreset/AiPresetImportModal';
import { createPortal } from 'react-dom';
import { useAnnualProgressCrossTopicsForTerm } from '../../hooks/useAnnualProgressCrossTopicsForTerm';
import { alignCrossTopicCounts, countCrossTopics } from '../../utils/annualCrossTopicCounts';
import type { AnnualCrossTopicDetailRow } from '../../utils/annualProgressCrossTopicExtract';
import {
  loadAnnualCrossTopicPlan,
  saveAnnualCrossTopicPlan,
  type AnnualCrossTopicPlanItem,
} from '../../services/annualCrossTopicPlanService';
import { requestAiCrossCurricular, type AiCrossRowInput } from '../../utils/aiCrossCurricular';
import { gradeNumbersFromClassName, type AiCrossTopicRow } from '../../utils/aiPresetExtract';
import { saveAnnualProgressProgramRecord } from '../../utils/annualProgressProgramRecordSave';
import { serializeCrossStorage } from '../../utils/programCellDisplayText';
import type { Grade } from '../../types/domain';

type Term = 1 | 2;

export type AnnualCrossTopicAiModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  programGradeId: string;
  schoolScopedCacheSyncReady: boolean;
};

type ResultRow = {
  key: string;
  term: Term;
  source: AnnualCrossTopicDetailRow;
  topic: string;
  content: string;
};

const CHUNK = 500;

function countedRows(rows: AnnualCrossTopicDetailRow[]): AnnualCrossTopicDetailRow[] {
  return rows.filter((r) => !r.isSyncTimetableMirror && r.crossTopic.trim());
}

function candidateRows(rows: AnnualCrossTopicDetailRow[]): AnnualCrossTopicDetailRow[] {
  return rows.filter(
    (r) =>
      !r.isSyncTimetableMirror &&
      !r.crossTopic.trim() &&
      !!r.contentRecordTarget &&
      !!(r.learning.trim() || r.unit.trim())
  );
}

function rowContext(r: AnnualCrossTopicDetailRow): string {
  return [r.templateLabel, r.unit].filter(Boolean).join(' · ');
}

function HoursField({
  label,
  value,
  onChange,
  helper,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  helper?: string;
}) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return (
    <div className="cp-cross-ai-field">
      <span className="cp-cross-ai-field-label">{label}</span>
      <div className="cp-cross-ai-stepper">
        <button type="button" aria-label={`${label} 줄이기`} onClick={() => onChange(String(Math.max(0, n - 1)))} disabled={n <= 0}>
          −
        </button>
        <input
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
          aria-label={label}
        />
        <span className="cp-cross-ai-stepper-suffix">시수</span>
        <button type="button" aria-label={`${label} 늘리기`} onClick={() => onChange(String(Math.min(999, n + 1)))}>
          +
        </button>
      </div>
      {helper ? <span className="cp-cross-ai-field-helper">{helper}</span> : null}
    </div>
  );
}

function RemoveIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function FunnelIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path d="M2.5 3h11l-4.2 5.2v4.3l-2.6 1.3V8.2z" fill="currentColor" />
    </svg>
  );
}

function TopicFilterMenu({
  topics,
  selected,
  onChange,
}: {
  topics: string[];
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);
  const active = selected != null;
  const toggle = (t: string) => {
    const base = new Set(selected ?? topics);
    if (base.has(t)) base.delete(t);
    else base.add(t);
    onChange(base.size === topics.length ? null : base);
  };
  return (
    <div className="cp-cross-ai-filter" ref={ref}>
      <button
        type="button"
        className={`cp-cross-ai-filter-btn${active ? ' is-active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        title={active ? `주제어 필터 적용 중 (${selected!.size}개)` : '주제어 필터'}
        onClick={() => setOpen((v) => !v)}
      >
        <FunnelIcon />
        {active ? <span className="cp-cross-ai-filter-badge">{selected!.size}</span> : null}
      </button>
      {open ? (
        <div className="cp-cross-ai-filter-menu" role="menu">
          <label className="cp-cross-ai-filter-item is-all">
            <input type="checkbox" checked={!active} onChange={() => onChange(null)} />
            전체
          </label>
          {topics.map((t) => (
            <label key={t} className="cp-cross-ai-filter-item">
              <input type="checkbox" checked={!active || selected!.has(t)} onChange={() => toggle(t)} />
              <span>{t}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type TopicForm = { id: string | null; topic: string; h1: string; h2: string };

function TopicEditModal({
  form,
  setForm,
  error,
  duplicate,
  counted,
  onSave,
  onDelete,
  onClose,
}: {
  form: TopicForm;
  setForm: (f: TopicForm) => void;
  error: string;
  duplicate: boolean;
  counted: { c1: number; c2: number } | null;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const invalid = !form.topic.trim() || duplicate;
  return (
    <div className="cp-modal-backdrop cp-cross-ai-sub-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cp-modal cp-cross-ai-topic-modal" role="dialog" aria-modal="true" aria-labelledby="cp-cross-ai-topic-title" onMouseDown={(e) => e.stopPropagation()}>
        <h3 id="cp-cross-ai-topic-title" className="cp-cross-ai-modal-head">{form.id ? '범교과 주제 수정' : '범교과 주제 등록'}</h3>
        <div className="cp-cross-ai-topic-body">
          <div className="cp-cross-ai-field">
            <label className="cp-cross-ai-field-label" htmlFor="cp-cross-ai-topic-input">범교과 주제어</label>
            <input
              id="cp-cross-ai-topic-input"
              className="cp-cross-ai-text-input"
              value={form.topic}
              maxLength={40}
              autoFocus
              placeholder="예) 안전·건강 교육"
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !invalid) onSave();
              }}
            />
            {duplicate ? <span className="cp-cross-ai-field-helper is-error">이미 등록된 주제어입니다.</span> : null}
          </div>
          <div className="cp-cross-ai-field-row">
            <HoursField label="1학기 편성 시수" value={form.h1} onChange={(v) => setForm({ ...form, h1: v })} helper={counted ? `현재 편성된 수 ${counted.c1}` : undefined} />
            <HoursField label="2학기 편성 시수" value={form.h2} onChange={(v) => setForm({ ...form, h2: v })} helper={counted ? `현재 편성된 수 ${counted.c2}` : undefined} />
          </div>
          {error ? <p className="cp-cross-ai-error" role="alert">{error}</p> : null}
        </div>
        <div className="cp-modal-footer cp-cross-ai-footer">
          {form.id ? (
            <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--danger" onClick={onDelete}>
              삭제
            </button>
          ) : null}
          <span className="cp-cross-ai-footer-spacer" />
          <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--outline" onClick={onClose}>
            닫기
          </button>
          <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--primary" onClick={onSave} disabled={invalid}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

/** 표 텍스트처럼 보이는 자동 높이 textarea */
function CellEditor({ value, onChange, onBlur, maxLength, label }: { value: string; onChange: (v: string) => void; onBlur?: () => void; maxLength: number; label: string }) {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      className="cp-cross-ai-cell-editor"
      rows={1}
      value={value}
      maxLength={maxLength}
      aria-label={label}
      onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))}
      onBlur={onBlur}
    />
  );
}

/**
 * 아래에 깔린 '1·2학기 범교과 주제 카운팅' 모달의 실제 렌더 높이(px)를 따라감.
 * 카운팅 모달 크기 규칙(.cp-annual-cross-topic-count-modal: 폭·min-height, 본문 max-height)은 CSS 에 그대로 두고,
 * 내용에 따라 늘어난 높이까지 ResizeObserver 로 동기화 → 같은 백드롭 정렬이라 위치도 같아짐.
 */
function useCountModalHeight(open: boolean, selfRef: React.RefObject<HTMLElement | null>): number | null {
  const [h, setH] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    if (!open) return;
    const target = Array.from(document.querySelectorAll<HTMLElement>('.cp-annual-cross-topic-count-modal')).find(
      (el) => el !== selfRef.current && !el.classList.contains('cp-cross-ai-modal')
    );
    if (!target) {
      setH(null);
      return;
    }
    const read = () => setH(Math.round(target.getBoundingClientRect().height));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(target);
    window.addEventListener('resize', read);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [open, selfRef]);
  return h;
}

const AnnualCrossTopicAiModal: React.FC<AnnualCrossTopicAiModalProps> = ({
  open,
  onClose,
  workspaceId,
  grades,
  academicYear,
  programGradeId,
  schoolScopedCacheSyncReady,
}) => {
  const [nonce, setNonce] = React.useState(0);
  const common = { enabled: open, workspaceId, grades, academicYear, programGradeId, schoolScopedCacheSyncReady, refreshNonce: nonce, includeEmptyCross: true };
  const t1 = useAnnualProgressCrossTopicsForTerm({ ...common, termNumber: 1 });
  const t2 = useAnnualProgressCrossTopicsForTerm({ ...common, termNumber: 2 });
  const loading = t1.loading || t2.loading;

  const [plan, setPlan] = React.useState<AnnualCrossTopicPlanItem[]>([]);
  const [planStorage, setPlanStorage] = React.useState<'db' | 'local'>('db');
  const [planError, setPlanError] = React.useState('');
  const [form, setForm] = React.useState<TopicForm | null>(null);
  const [topicImportOpen, setTopicImportOpen] = React.useState(false);
  const [resultsOpen, setResultsOpen] = React.useState(false);
  /** 결과 표 표시 순서(주제 등록 순 → 날짜·교시 순). 입력 중에는 고정, blur 시 재정렬 */
  const [resultOrder, setResultOrder] = React.useState<string[]>([]);
  const [topicFilter, setTopicFilter] = React.useState<Set<string> | null>(null);
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiError, setAiError] = React.useState('');
  const [results, setResults] = React.useState<ResultRow[] | null>(null);
  const [applyBusy, setApplyBusy] = React.useState(false);
  const [applyMsg, setApplyMsg] = React.useState('');
  const abortRef = React.useRef<AbortController | null>(null);
  const selfRef = React.useRef<HTMLDivElement | null>(null);
  const countModalHeight = useCountModalHeight(open, selfRef);
  const matchedHeightStyle: React.CSSProperties | undefined =
    countModalHeight != null ? { height: countModalHeight, minHeight: 0, maxHeight: 'none' } : undefined;

  const gid = programGradeId.trim();
  React.useEffect(() => {
    if (!open || !workspaceId || !gid) return;
    let alive = true;
    setPlanError('');
    loadAnnualCrossTopicPlan(workspaceId, gid, academicYear)
      .then((r) => {
        if (!alive) return;
        setPlan(r.items);
        setPlanStorage(r.storage);
      })
      .catch((e) => alive && setPlanError(e instanceof Error ? e.message : '주제 목록을 불러오지 못했습니다.'));
    return () => {
      alive = false;
    };
  }, [academicYear, gid, open, workspaceId]);

  React.useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      setForm(null);
      setTopicImportOpen(false);
      setResults(null);
      setResultsOpen(false);
      setAiError('');
      setApplyMsg('');
      setAiBusy(false);
    }
  }, [open]);

  const counts = React.useMemo(() => {
    const c1 = countCrossTopics(countedRows(t1.detailRows).map((r) => r.crossTopic));
    const c2 = countCrossTopics(countedRows(t2.detailRows).map((r) => r.crossTopic));
    return { c1, c2, aligned: alignCrossTopicCounts(c1, c2) };
  }, [t1.detailRows, t2.detailRows]);

  const remainingFor = React.useCallback(
    (item: AnnualCrossTopicPlanItem, term: Term) => {
      const target = term === 1 ? item.term1Hours : item.term2Hours;
      const done = (term === 1 ? counts.c1 : counts.c2).get(item.topic) ?? 0;
      return Math.max(0, target - done);
    },
    [counts]
  );

  const boardTopics = React.useMemo(() => {
    const names = new Set<string>(plan.map((p) => p.topic));
    counts.aligned.forEach((a) => names.add(a.topic));
    return Array.from(names);
  }, [counts.aligned, plan]);

  const persistPlan = async (next: AnnualCrossTopicPlanItem[]) => {
    setPlanError('');
    const prev = plan;
    setPlan(next);
    try {
      setPlanStorage(await saveAnnualCrossTopicPlan(workspaceId, gid, academicYear, next));
    } catch (e) {
      setPlan(prev);
      setPlanError(e instanceof Error ? e.message : '저장하지 못했습니다.');
    }
  };

  const submitForm = () => {
    if (!form) return;
    const topic = form.topic.trim();
    if (!topic) return setPlanError('범교과 주제어를 입력해 주세요.');
    if (plan.some((p) => p.topic === topic && p.id !== form.id)) return setPlanError('이미 등록된 주제어입니다.');
    const h1 = Math.max(0, Math.floor(Number(form.h1) || 0));
    const h2 = Math.max(0, Math.floor(Number(form.h2) || 0));
    const next = form.id
      ? plan.map((p) => (p.id === form.id ? { ...p, topic, term1Hours: h1, term2Hours: h2 } : p))
      : [...plan, { id: `t${Date.now().toString(36)}`, topic, term1Hours: h1, term2Hours: h2 }];
    setForm(null);
    void persistPlan(next);
  };

  const applyImportedTopics = (rows: AiCrossTopicRow[]) => {
    const next = plan.map((p) => ({ ...p }));
    let added = 0;
    rows.forEach((row, i) => {
      const topic = row.topic.trim().slice(0, 40);
      if (!topic) return;
      const h1 = Math.max(0, Math.min(999, Math.floor(Number(row.term1_hours) || 0)));
      const h2 = Math.max(0, Math.min(999, Math.floor(Number(row.term2_hours) || 0)));
      const hit = next.find((p) => p.topic === topic);
      if (hit) {
        if (h1 > 0) hit.term1Hours = h1;
        if (h2 > 0) hit.term2Hours = h2;
        return;
      }
      added += 1;
      next.push({ id: `t${Date.now().toString(36)}${i}`, topic, term1Hours: h1, term2Hours: h2 });
    });
    if (added === 0 && next.every((p, i) => p.term1Hours === plan[i]?.term1Hours && p.term2Hours === plan[i]?.term2Hours && p.topic === plan[i]?.topic)) return;
    setPlanError('');
    void persistPlan(next);
  };

  const deleteFormTopic = () => {
    if (!form?.id) return;
    const item = plan.find((p) => p.id === form.id);
    if (!item || !window.confirm(`'${item.topic}' 주제를 삭제할까요? (이미 반영된 범교과는 그대로 남습니다)`)) return;
    setForm(null);
    void persistPlan(plan.filter((p) => p.id !== item.id));
  };

  const openTopicEditor = (topic: string) => {
    const p = plan.find((x) => x.topic === topic);
    if (!p) return;
    setPlanError('');
    setForm({ id: p.id, topic: p.topic, h1: String(p.term1Hours), h2: String(p.term2Hours) });
  };

  const runAi = async () => {
    setAiError('');
    setApplyMsg('');
    setAiBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const out: ResultRow[] = [];
      for (const term of [1, 2] as Term[]) {
        const left = new Map(plan.map((p) => [p.topic, remainingFor(p, term)]));
        if (!Array.from(left.values()).some((n) => n > 0)) continue;
        const cands = candidateRows(term === 1 ? t1.detailRows : t2.detailRows);
        for (let i = 0; i < cands.length; i += CHUNK) {
          const topics = Array.from(left.entries()).filter(([, n]) => n > 0).map(([topic, remaining]) => ({ topic, remaining }));
          if (!topics.length) break;
          const chunk = cands.slice(i, i + CHUNK);
          const idMap = new Map<string, AnnualCrossTopicDetailRow>();
          const rows: AiCrossRowInput[] = chunk.map((r, j) => {
            const id = `r${j + 1}`;
            idMap.set(id, r);
            return { id, subject: r.templateLabel, unit: r.unit, learning: r.learning };
          });
          const items = await requestAiCrossCurricular(
            { termNumber: term, semesterInfo: `${academicYear}학년도 ${term}학기`, topics, rows },
            ctrl.signal
          );
          for (const it of items) {
            const src = idMap.get(it.id);
            if (!src) continue;
            left.set(it.topic, (left.get(it.topic) ?? 0) - 1);
            out.push({ key: `${term}:${src.annualCellKey ?? it.id}`, term, source: src, topic: it.topic, content: it.content });
          }
        }
      }
      setResults(out);
      setTopicFilter(null);
      if (out.length) setResultsOpen(true);
      if (!out.length) setAiError('배치할 남은 시수가 없거나 어울리는 수업을 찾지 못했습니다.');
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        setAiError(e instanceof Error ? e.message : 'AI 요청에 실패했습니다.');
      }
    } finally {
      setAiBusy(false);
    }
  };

  const applyResults = async () => {
    if (!results?.length) return;
    setApplyBusy(true);
    setApplyMsg('');
    let ok = 0;
    const failed: ResultRow[] = [];
    const doneTargets = new Set<string>();
    for (const r of results) {
      const target = r.source.contentRecordTarget;
      const topic = r.topic.trim();
      const tId =
        target?.fixedRowId ??
        target?.scopedRowId ??
        (target?.scopedScopeKey ? `${target.scopedScopeKey}:${target.scopedOrderIndex}` : target?.legacyCell?.id ?? '');
      const tKey = target ? `${r.term}|${target.kind}|${tId}` : '';
      if (!target || !topic || doneTargets.has(tKey)) {
        failed.push(r);
        continue;
      }
      try {
        await saveAnnualProgressProgramRecord({
          workspaceId,
          gradeId: gid,
          academicYear,
          termNumber: r.term,
          annualCellKey: r.source.annualCellKey ?? '',
          target,
          patch: { record_cross_curricular_text: serializeCrossStorage(topic, r.content) },
        });
        doneTargets.add(tKey);
        ok += 1;
      } catch {
        failed.push(r);
      }
    }
    setApplyBusy(false);
    setResults(failed.length ? failed : null);
    if (!failed.length) setResultsOpen(false);
    setApplyMsg(
      failed.length
        ? `${ok}건 반영, ${failed.length}건 실패(아래 남은 행). 프로그램 원본이 없는 수업일 수 있습니다.`
        : `${ok}건을 범교과에 반영했습니다.`
    );
    setNonce((n) => n + 1);
  };

  const editResult = (key: string, patch: Partial<Pick<ResultRow, 'topic' | 'content'>>) =>
    setResults((prev) => prev?.map((r) => (r.key === key ? { ...r, ...patch } : r)) ?? null);
  const resortResults = React.useCallback(() => {
    setResults((prev) => {
      if (!prev) return prev;
      const rank = new Map(plan.map((p, i) => [p.topic, i]));
      const idx = new Map(prev.map((r, i) => [r.key, i]));
      const sorted = [...prev].sort((a, b) => {
        const ra = rank.get(a.topic.trim()) ?? plan.length;
        const rb = rank.get(b.topic.trim()) ?? plan.length;
        if (ra !== rb) return ra - rb;
        if (ra === plan.length && a.topic !== b.topic) return a.topic.localeCompare(b.topic, 'ko');
        const da = `${a.source.mondayIso}|${a.source.weekdayKey}|${String(a.source.slotIndex).padStart(2, '0')}|${String(a.source.seq).padStart(5, '0')}`;
        const db = `${b.source.mondayIso}|${b.source.weekdayKey}|${String(b.source.slotIndex).padStart(2, '0')}|${String(b.source.seq).padStart(5, '0')}`;
        return da < db ? -1 : da > db ? 1 : (idx.get(a.key) ?? 0) - (idx.get(b.key) ?? 0);
      });
      setResultOrder(sorted.map((r) => r.key));
      return prev;
    });
  }, [plan]);

  React.useEffect(() => {
    if (resultsOpen) resortResults();
    // 결과 세트가 바뀔 때(생성·반영 후)만 재정렬
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsOpen, results?.length]);

  const removeResult = (key: string) => setResults((prev) => prev?.filter((r) => r.key !== key) ?? null);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || applyBusy) return;
      e.preventDefault();
      if (topicImportOpen) return setTopicImportOpen(false);
      if (form) return setForm(null);
      if (resultsOpen) return setResultsOpen(false);
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [applyBusy, form, onClose, open, resultsOpen, topicImportOpen]);

  if (!open) return null;

  const planByTopic = new Map(plan.map((p) => [p.topic, p]));
  const anyRemaining = plan.some((p) => remainingFor(p, 1) > 0 || remainingFor(p, 2) > 0);
  const formDuplicate = !!form && plan.some((p) => p.topic === form.topic.trim() && p.id !== form.id);
  const orderRank = new Map(resultOrder.map((k, i) => [k, i]));
  const orderedResults = [...(results ?? [])].sort(
    (a, b) => (orderRank.get(a.key) ?? 1e9) - (orderRank.get(b.key) ?? 1e9)
  );
  const resultTopics = (() => {
    const present = new Set((results ?? []).map((r) => r.topic.trim()).filter(Boolean));
    const ordered = plan.map((p) => p.topic).filter((t) => present.has(t));
    present.forEach((t) => {
      if (!ordered.includes(t)) ordered.push(t);
    });
    return ordered;
  })();
  const resultCount = (term: Term) => results?.filter((r) => r.term === term).length ?? 0;

  return createPortal(
    <>
      <div
        className="cp-modal-backdrop cp-annual-cross-topic-count-backdrop cp-cross-ai-stack-backdrop"
        role="presentation"
        onMouseDown={(ev) => {
          if (topicImportOpen) return;
          if (ev.target === ev.currentTarget && !applyBusy) onClose();
        }}
      >
        <div
          ref={selfRef}
          style={matchedHeightStyle}
          className="cp-modal cp-annual-cross-topic-count-modal cp-cross-ai-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-cross-ai-title"
          onMouseDown={(ev) => ev.stopPropagation()}
        >
          <h3 id="cp-cross-ai-title">1·2학기 AI 범교과 자동 구성</h3>
          <div className="cp-cross-ai-actions">
            <button
              type="button"
              className="cp-cross-ai-btn cp-cross-ai-btn--outline cp-cross-ai-action"
              onClick={() => {
                setPlanError('');
                setForm({ id: null, topic: '', h1: '', h2: '' });
              }}
            >
              범교과 주제 등록
            </button>
            <button
              type="button"
              className="cp-ai-feedback-btn cp-cross-ai-action cp-cross-ai-action--import"
              onClick={() => {
                setPlanError('');
                setTopicImportOpen(true);
              }}
            >
              <AiSparkIcon />
              범교과 주제 등록 AI 자료 입력
            </button>
            <button
              type="button"
              className="cp-ai-feedback-btn cp-cross-ai-action"
              onClick={() => (results?.length && !aiBusy ? setResultsOpen(true) : void runAi())}
              disabled={aiBusy || loading || applyBusy || (!anyRemaining && !results?.length)}
              title={!plan.length ? '먼저 범교과 주제를 등록해 주세요.' : undefined}
            >
              <AiSparkIcon />
              {aiBusy ? 'AI 구성 중…' : results?.length ? `AI 구성안 보기 (${results.length})` : 'AI 범교과 구성'}
            </button>
          </div>
          {planStorage === 'local' ? <p className="cp-cross-ai-hint">DB 테이블 미적용: 등록한 주제는 이 브라우저에만 저장됩니다.</p> : null}
          {planError && !form ? <p className="cp-cross-ai-error" role="alert">{planError}</p> : null}
          {aiError ? <p className="cp-cross-ai-error" role="alert">{aiError}</p> : null}
          {applyMsg ? <p className="cp-cross-ai-hint" role="status">{applyMsg}</p> : null}
          <div className="cp-modal-body cp-annual-cross-topic-count-body">
            {loading ? <p className="page-desc" role="status" aria-busy="true">1·2학기 데이터 불러오는 중…</p> : null}
            {!loading ? (
              <div className="cp-annual-cross-topic-count-board">
                {([
                  { key: 'term1', title: '1학기' },
                  { key: 'term2', title: '2학기' },
                  { key: 'annual', title: '연간' },
                ] as const).map(({ key, title }) => (
                  <div key={key} className="cp-annual-cross-topic-count-column">
                    <section className={`cp-annual-cross-topic-count-section cp-annual-cross-topic-count-section--${key}`} aria-label={title}>
                      <h4 className="cp-annual-cross-topic-count-section-title">{title}</h4>
                      <div className="cp-annual-cross-topic-count-section-body">
                        {boardTopics.map((topic) => {
                          const c1 = counts.c1.get(topic) ?? 0;
                          const c2 = counts.c2.get(topic) ?? 0;
                          const p = planByTopic.get(topic);
                          const done = key === 'term1' ? c1 : key === 'term2' ? c2 : c1 + c2;
                          const goal = p ? (key === 'term1' ? p.term1Hours : key === 'term2' ? p.term2Hours : p.term1Hours + p.term2Hours) : null;
                          if (!done && !goal) return <div key={topic} className="cp-annual-cross-topic-count-slot" aria-hidden />;
                          const cls = `cp-annual-cross-topic-count-block cp-cross-ai-block${p ? ' is-registered' : ''}${goal != null && done >= goal ? ' is-done' : ''}`;
                          const inner = (
                            <>
                              <span className="cp-annual-cross-topic-count-block-topic">{topic}</span>
                              <span className="cp-annual-cross-topic-count-block-num">{goal != null ? `${done}/${goal}` : done}</span>
                            </>
                          );
                          return (
                            <div key={topic} className="cp-annual-cross-topic-count-slot">
                              {p ? (
                                <button type="button" className={cls} title={`${topic} ${done}/${goal} — 클릭하여 수정`} onClick={() => openTopicEditor(topic)}>
                                  {inner}
                                </button>
                              ) : (
                                <div className={cls} title={`${topic} ${done}`}>
                                  {inner}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="cp-modal-footer cp-cross-ai-footer">
            <span className="cp-cross-ai-footer-spacer" />
            <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--outline" onClick={onClose} disabled={applyBusy}>
              닫기
            </button>
          </div>
        </div>
      </div>

      {form ? (
        <TopicEditModal
          form={form}
          setForm={setForm}
          error={planError}
          duplicate={formDuplicate}
          counted={form.id ? { c1: counts.c1.get(form.topic.trim()) ?? 0, c2: counts.c2.get(form.topic.trim()) ?? 0 } : null}
          onSave={submitForm}
          onDelete={deleteFormTopic}
          onClose={() => setForm(null)}
        />
      ) : null}

      {resultsOpen && results?.length ? (
        <div className="cp-modal-backdrop cp-cross-ai-sub-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !applyBusy && setResultsOpen(false)}>
          <div className="cp-modal cp-cross-ai-result-modal" style={matchedHeightStyle} role="dialog" aria-modal="true" aria-labelledby="cp-cross-ai-result-title" onMouseDown={(e) => e.stopPropagation()}>
            <h3 id="cp-cross-ai-result-title" className="cp-cross-ai-modal-head">AI 범교과 구성안</h3>
            <p className="cp-cross-ai-hint">주제어·학습내용을 클릭해 바로 고칠 수 있어요. 필요 없는 행은 ✕로 빼 주세요.</p>
            {applyMsg ? <p className="cp-cross-ai-hint" role="status">{applyMsg}</p> : null}
            <div className="cp-cross-ai-result-scroll">
              {([1, 2] as Term[]).map((term) => {
                const all = orderedResults.filter((r) => r.term === term);
                if (!all.length) return null;
                const list = topicFilter ? all.filter((r) => topicFilter.has(r.topic.trim())) : all;
                return (
                  <section key={term} className="cp-cross-ai-result">
                    <h4>
                      {term}학기{' '}
                      <span className="cp-cross-ai-result-count">
                        {topicFilter ? `${list.length} / ${all.length}건` : `${all.length}건`}
                      </span>
                    </h4>
                    <table className="cp-cross-ai-table">
                      <colgroup>
                        <col className="cp-cross-ai-col-ctx" />
                        <col className="cp-cross-ai-col-learning" />
                        <col className="cp-cross-ai-col-topic" />
                        <col />
                        <col className="cp-cross-ai-col-remove" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>편제·단원</th>
                          <th>학습내용</th>
                          <th className={topicFilter ? 'is-filtered' : undefined}>
                            <span className="cp-cross-ai-th-filter">
                              범교과 주제어
                              <TopicFilterMenu topics={resultTopics} selected={topicFilter} onChange={setTopicFilter} />
                            </span>
                          </th>
                          <th>범교과 학습내용</th>
                          <th aria-label="삭제" />
                        </tr>
                      </thead>
                      <tbody>
                        {!list.length ? (
                          <tr>
                            <td colSpan={5} className="cp-cross-ai-empty">필터에 해당하는 행이 없습니다.</td>
                          </tr>
                        ) : null}
                        {list.map((r) => (
                          <tr key={r.key}>
                            <td>
                              <div className="cp-cross-ai-ctx-main">{r.source.templateLabel || '-'}</div>
                              <div className="cp-cross-ai-ctx">{r.source.unit || ''}</div>
                              <div className="cp-cross-ai-ctx">{r.source.dateLabel}({r.source.weekdayLabel}) {r.source.periodLabel}</div>
                            </td>
                            <td>{r.source.learning || '-'}</td>
                            <td className="cp-cross-ai-editable">
                              <CellEditor value={r.topic} maxLength={40} label="범교과 주제어" onChange={(v) => editResult(r.key, { topic: v })} onBlur={resortResults} />
                            </td>
                            <td className="cp-cross-ai-editable">
                              <CellEditor value={r.content} maxLength={200} label="범교과 학습내용" onChange={(v) => editResult(r.key, { content: v })} />
                            </td>
                            <td>
                              <button type="button" className="cp-cross-ai-remove" aria-label="이 행 빼기" title="이 행 빼기" onClick={() => removeResult(r.key)}>
                                <RemoveIcon />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>
                );
              })}
            </div>
            <div className="cp-modal-footer cp-cross-ai-footer">
              <span className="cp-cross-ai-summary">
                1학기 {resultCount(1)}건 · 2학기 {resultCount(2)}건 · 전체 {results.length}건 반영
                {topicFilter ? ' (필터는 보기 전용)' : ''}
              </span>
              <span className="cp-cross-ai-footer-spacer" />
              <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--outline" onClick={() => setResultsOpen(false)} disabled={applyBusy}>
                닫기
              </button>
              <button type="button" className="cp-cross-ai-btn cp-cross-ai-btn--primary" onClick={() => void applyResults()} disabled={applyBusy || aiBusy}>
                {applyBusy ? '반영 중…' : `범교과 반영하기 (전체 ${results.length}건)`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {topicImportOpen ? (
        <AiPresetImportModal
          kind="crossTopics"
          title="범교과 주제 등록 AI 자료 입력"
          resumeKey={`crossTopics:${workspaceId}:${gid}:${academicYear}`}
          backdropClassName="cp-cross-ai-sub-backdrop"
          defaultGrade={
            gradeNumbersFromClassName(grades.find((g) => g.id === gid)?.name ?? '')[0] ?? 1
          }
          schoolYear={academicYear}
          existingTopics={plan.map((p) => p.topic)}
          onClose={() => setTopicImportOpen(false)}
          onApply={applyImportedTopics}
        />
      ) : null}
    </>,
    document.body
  );
};

export default AnnualCrossTopicAiModal;
